import { createRef, ReactNode, RefObject, useEffect } from 'react';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Container from '@components/global/Container';
import Layout from '@components/global/Layout';
import getAllPromoTickerData from '@adapters/prismic/fetching/promos/getAllPromoTickerData';
import getBannerStripGlobalData from '@adapters/prismic/fetching/banner-strip-global/getBannerStripGlobalData';
import { PromoTicker } from '@adapters/prismic/types/promoTicker';
import type { GetStaticPropsContext } from 'next';
import { getAllStoresDataForSetMyStore } from '@adapters/prismic/fetching/stores';
import Heading from '@components/ui/Heading';
import { useTranslation } from 'next-i18next';
import { sortBrands } from '@adapters/bigcommerce/transforms/brands';
import { SortedPrismicBrand } from '@adapters/prismic/types/brand';
import { handlePromoDetailsTracking } from '@utils/gtm';

import { altLocale } from '@utils/locale';
import {
  localeAltPathAbs,
  localePathAbs,
  localeAltPath,
  generateBrandURL,
} from '@utils/url';
import { MEC_TITLE } from '@utils/constants';
import brandsData from '../../../public/brands.json';
import { getPreviewRef } from '@adapters/prismic/utils/getPreviewRef';

import styles from './Brands.module.scss';
import { BRANDS, MARQUES } from '@utils/plp/constants';
import { FEATURE_FLAG_PROMO_BAR_SERVER_SIDE } from '@config/feature-flag';
import getPromoBarData from '@adapters/prismic/fetching/promos/getPromoBarData';

interface BrandsAllPageProps {
  canonicalUrl: string;
  langAltUrl: string;
  promoMessages?: PromoTicker[];
  sortedBrands: SortedPrismicBrand[];
  xDefault: string;
}

export async function getStaticPaths() {
  return {
    paths: [{ params: { locale: 'en' } }, { params: { locale: 'fr' } }],
    fallback: false,
  };
}

export async function getStaticProps({
  params,
  previewData,
  preview = false,
}: GetStaticPropsContext<{ locale: string; code: string }>) {
  const locale = params?.locale === 'fr' ? 'fr' : 'en';
  if (!locale) {
    throw new Error('No locale specified for page');
  }

  const previewRef = getPreviewRef(preview, previewData);

  const [
    stores,
    promoMessages,
    bannerStripData,
    serverSideTranslation,
    promoBarData,
  ] = await Promise.allSettled([
    getAllStoresDataForSetMyStore(locale),
    !FEATURE_FLAG_PROMO_BAR_SERVER_SIDE &&
      locale &&
      getAllPromoTickerData(locale),
    getBannerStripGlobalData(locale, preview ? previewRef : undefined),
    serverSideTranslations(locale, ['common', 'allBrands']),
    FEATURE_FLAG_PROMO_BAR_SERVER_SIDE && locale && getPromoBarData(locale),
  ]).then((results) =>
    results.map((promise) => {
      if (
        promise.status === 'rejected' ||
        typeof promise.value === 'undefined'
      ) {
        console.error('promise rejected or returning undefined', promise);
        return null;
      }
      return promise.value;
    })
  );

  const brandsArray = Object.entries(brandsData).map(([url, name]) => ({
    slug: url,
    name,
  }));

  const sortedBrands = sortBrands(brandsArray);

  const canonicalUrl = localePathAbs(
    locale,
    locale === 'en' ? `/${BRANDS}` : `/${MARQUES}`
  );
  const langAltUrl = localeAltPathAbs(
    locale,
    locale === 'en' ? `/${MARQUES}` : `/${BRANDS}`
  );
  const xDefault = locale === 'en' ? canonicalUrl : langAltUrl;

  const alternateLocaleUrl = localeAltPath(
    locale,
    locale === 'en' ? `/${MARQUES}` : `/${BRANDS}`
  );

  return {
    props: {
      stores,
      canonicalUrl,
      langAltUrl,
      alternateLocaleUrl,
      sortedBrands,
      xDefault,
      ...serverSideTranslation,
      promoMessages,
      promoBarData,
      bannerStripData,
    },
  };
}

export default function BrandsAll({
  canonicalUrl,
  langAltUrl,
  sortedBrands,
  xDefault,
}: BrandsAllPageProps): ReactNode {
  const { query } = useRouter();
  const locale = (query.locale as string) || 'en';
  const { t } = useTranslation('allBrands');
  const title = t('allBrands.title');

  const refs = sortedBrands.reduce(
    (acc: Record<string, RefObject<HTMLDivElement | null>>, value) => {
      acc[value.title] = createRef();
      return acc;
    },
    {}
  );

  const handleClick = (name: string) =>
    refs[name].current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });

  const handleScrollToTop = () => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    handlePromoDetailsTracking('promoDetails');
  }, []);

  return (
    <>
      <NextSeo
        title={`${title} ${MEC_TITLE}`}
        canonical={canonicalUrl}
        languageAlternates={[
          { hrefLang: altLocale(locale), href: langAltUrl },
          { hrefLang: String(locale), href: canonicalUrl },
          { hrefLang: 'x-default', href: xDefault },
        ]}
        openGraph={{
          title,
          locale,
        }}
      />
      <Container>
        <div>
          <Heading semanticLevel="h1" styleLevel="2" className={styles.header}>
            {t('allBrands.title')}
          </Heading>
          <ul className={styles.brandsIndex}>
            {sortedBrands?.map((letter) => (
              <li key={`brand-index-${letter.title}`}>
                <button
                  type="button"
                  className={styles.brandIndexButton}
                  onClick={() => handleClick(letter?.title)}
                  disabled={letter?.links?.length === 0}
                >
                  {letter.title}
                </button>
              </li>
            ))}
          </ul>
          {sortedBrands.map((letterGroup) => (
            <div
              ref={refs[letterGroup.title]}
              id={letterGroup.title}
              className={styles.brandContainer}
              key={`brand-category-${letterGroup.title}`}
            >
              <div className={styles.brandItemWrapper}>
                <div className={styles.headerContainer}>
                  <Heading semanticLevel="h2" styleLevel="2">
                    {letterGroup.title}
                  </Heading>
                  <button
                    type="button"
                    className={styles.backToTopButton}
                    onClick={handleScrollToTop}
                  >
                    {t('allBrands.back.to.top')}
                  </button>
                </div>
                <ul className={styles.brandsList}>
                  {letterGroup.links.map((brand) => (
                    <li
                      className={styles.brandListItem}
                      key={`brand-link-${brand.name}`}
                    >
                      <a href={generateBrandURL(brand?.slug, locale)}>
                        {brand.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
              <div className={styles.backToToButtonBottom}>
                <button
                  type="button"
                  className={styles.backToTopButtonFooter}
                  onClick={handleScrollToTop}
                >
                  {t('allBrands.back.to.top')}
                </button>
              </div>
            </div>
          ))}
        </div>
      </Container>
    </>
  );
}

BrandsAll.Layout = Layout;
